import styled from 'styled-components';

import { Spinner } from '../../../../../../components/atoms/spinner/spinner';
import { Drawer } from '../../../../../../components/molecules/drawer/drawer';
import { ErrorState } from '../../../../../../components/molecules/error-state/error-state';
import { ClienteDetail } from '../../../../../../types/cliente';
import {
  ClienteVendaDetail,
  StatusVenda,
} from '../../../../../../types/cliente-venda';
import { PayloadTecnicoSection } from '../produtos/sections/payload-tecnico-section';
import { formatPreco } from './format';
import { CheckoutSection } from './sections/checkout-section';
import { CompradorSection } from './sections/comprador-section';
import { MetadadosSection } from './sections/metadados-section';
import { PagamentoSection } from './sections/pagamento-section';
import { ProdutoSection } from './sections/produto-section';
import { RecebiveisSection } from './sections/recebiveis-section';
import { ResumoSection } from './sections/resumo-section';
import { VendaStatusBadge } from './venda-status-badge';
import { VendaUpsellBadge } from './venda-upsell-badge';

interface Props {
  isOpen: boolean;
  cliente: ClienteDetail | null;
  detail: ClienteVendaDetail | null;
  status: 'idle' | 'loading' | 'error';
  errorMessage?: string | null;
  onClose: () => void;
}

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HeaderInfo = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.secondary};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const buyerName = (detail: ClienteVendaDetail): string =>
  `${detail.comprador.nome ?? ''} ${detail.comprador.sobrenome ?? ''}`.trim() ||
  '—';

const drawerTitle = (detail: ClienteVendaDetail | null): string =>
  detail ? `Venda #${detail.id.slice(0, 8)}` : 'Detalhes da venda';

export const VendaDetailDrawer = ({
  isOpen,
  cliente,
  detail,
  status,
  errorMessage,
  onClose,
}: Props) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    width={720}
    title={drawerTitle(detail)}
    description={
      detail
        ? formatPreco(detail.resumo.valorPago, detail.resumo.moedaSigla)
        : undefined
    }
  >
    {status === 'loading' && (
      <Loading>
        <Spinner /> Carregando detalhes da venda…
      </Loading>
    )}

    {status === 'error' && (
      <ErrorState
        title="Não foi possível carregar a venda"
        description={errorMessage ?? 'Tente novamente mais tarde.'}
      />
    )}

    {detail && status !== 'loading' && (
      <>
        <HeaderInfo>
          <TopRow>
            <div>
              <Title>{drawerTitle(detail)}</Title>
              <Subtitle>
                Cliente: {cliente?.nome ?? '—'}
                {cliente?.email ? ` · ${cliente.email}` : ''}
              </Subtitle>
              <Subtitle>
                Comprador: {buyerName(detail)}
                {detail.comprador.email ? ` · ${detail.comprador.email}` : ''}
              </Subtitle>
            </div>
            <BadgeRow>
              <VendaStatusBadge
                status={(detail.resumo.status as StatusVenda | null) ?? null}
                descricao={detail.resumo.statusDescricao}
              />
              <VendaUpsellBadge upsell={detail.resumo.upsell} />
            </BadgeRow>
          </TopRow>
          <Subtitle>
            {formatPreco(detail.resumo.valorPago, detail.resumo.moedaSigla)}
            {detail.resumo.metodoPagamentoDescricao
              ? ` · ${detail.resumo.metodoPagamentoDescricao}`
              : ''}
            {detail.resumo.gatewayDescricao
              ? ` · ${detail.resumo.gatewayDescricao}`
              : ''}
          </Subtitle>
        </HeaderInfo>

        <Sections>
          <ResumoSection
            vendaId={detail.id}
            createdAtIso={detail.createdAt}
            data={detail.resumo}
          />
          <CompradorSection data={detail.comprador} />
          <ProdutoSection data={detail.produto} />
          <CheckoutSection data={detail.checkout} />
          <RecebiveisSection
            recebiveis={detail.recebiveis}
            totais={detail.totaisRecebiveis}
            moedaSigla={detail.resumo.moedaSigla}
          />
          <MetadadosSection data={detail.metadados} />
          <PagamentoSection
            data={detail.pagamento}
            metodoPagamentoDescricao={detail.resumo.metodoPagamentoDescricao}
          />
          <PayloadTecnicoSection payload={detail.payloadTecnico} />
        </Sections>
      </>
    )}
  </Drawer>
);
